import {
	CustomerServiceManagementIcon,
	JiraIcon,
	JiraServiceManagementIcon,
	LogoProps,
} from '@atlaskit/logo-next';

import { ProductKey } from '@/components/signup/utils/products';

import { JiraTemplateChoice } from '../../hooks/use-jira-template-choice';
import {
	CUSTOMER_SERVICE_MANAGEMENT_KEY,
	HR_SERVICE_MANAGEMENT_KEY,
	IT_SERVICE_MANAGEMENT_KEY,
	KANBAN_TEAM_MANAGED_KEY,
	SCRUM_TEAM_MANAGED_KEY,
} from '../../models/percieved-provisioning';

type TemplateCategories =
	| 'marketingProjectManagement'
	| 'campaignManagement'
	| 'contentManagement'
	| 'processControl'
	| 'projectManagement'
	| 'procurement'
	| 'softwareDevelopmentKanban'
	| 'softwareDevelopmentScrum'
	| 'softwareDevelopmentProjectManagement'
	| 'productManagementScrum'
	| 'productManagementProjectManagement'
	| 'productManagementBusinessCollaboration'
	| 'designKanban'
	| 'designWebDesignProcess'
	| 'designScrum'
	| 'projectManagementKanban'
	| 'projectManagementProjectManagement'
	| 'projectManagementScrum'
	| 'itSupportKanban'
	| 'itSupportScrum'
	| 'itSupportProjectManagement'
	| 'humanResourcesProjectManagement'
	| 'humanResourcesNewEmployeeOnboarding'
	| 'humanResourcesRecruitment'
	| 'customerServiceKanban'
	| 'customerServiceScrum'
	| 'customerServiceProjectManagement'
	| 'legalProcessControl'
	| 'legalProjectManagement'
	| 'legalIpInfringement'
	| 'financeProjectManagement'
	| 'financeProcurement'
	| 'financeBudgetCreation'
	| 'salesProjectManagement'
	| 'salesSalesPipeline'
	| 'salesLeadTracking'
	| 'dataScienceKanban'
	| 'dataScienceScrum'
	| 'dataScienceProjectManagement'
	| 'otherScrum'
	| 'otherKanban'
	| 'otherProjectManagement'
	| 'itSupportServiceManagement'
	| 'humanResourcesServiceManagement'
	| 'customerServiceManagement';

export type TemplateChoices = {
	[K in TemplateCategories]: TemplateChoice;
};

export type TemplateChoice = {
	imageSrc: string;
	mobileImgSrc: string;
	title: string;
	subtext: string;
	templateId: string;
	templateType: JiraTemplateChoice | undefined;
	/** Product that powers the template. Undefined means Jira. */
	productKey?: ProductKey;
};

export type TemplateOption = {
	option: TemplateChoice;
	metadata?: TemplateOptionMetadata;
};

export type TemplateOptionMetadata = {
	persona?: string;
	jtbd?: string;
	products?: ProductKey[];
};

export const productKeyAttributes: Partial<
	Record<ProductKey, { icon: React.ComponentType<LogoProps> }>
> = {
	[ProductKey.JIRA_SOFTWARE]: {
		icon: JiraIcon,
	},
	[ProductKey.JIRA_SERVICE_MANAGEMENT]: {
		icon: JiraServiceManagementIcon,
	},
	[ProductKey.JIRA_CUSTOMER_SERVICE]: {
		icon: CustomerServiceManagementIcon,
	},
};

export const templateChoices: TemplateChoices = {
	campaignManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/10ppastefs72qh5685o35gu1jxnwk321.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/1b77eof7q055iutk2703f1e41625p30f.webp',
		title: 'Campaign management',
		subtext: 'Work towards campaign launches, tracking deadlines and tasks along the way',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	marketingProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/320juw1ao165rgl2q2n1b00v6ri868i2.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/td46stfve7x68r38ina7j52616n566c1.webp',
		title: 'Marketing project management',
		subtext: 'Manage marketing projects efficiently, visualize tasks, and see team workload',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	contentManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/26jgte23bndr258r245r1ht554o11611.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/6wus57n08s4lb1lb7l54bl877w68ir60.webp',
		title: 'Content management',
		subtext: 'Oversee your marketing content lifecycle from draft to delivery',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	processControl: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/djkwu8yfgdb74rjb8ek207qi4uqs3ov4.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/76h2cq112s5e8gj87dvnl8bt7ij3gg04.webp',
		title: 'Process control',
		subtext: 'Create, monitor, and iterate on processes in one place',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	projectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/s6ff5qxwo5vvxc86tb78fac2yp4bq421.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/rm5grquxlgb2v4s1evg165ji01c1n8a2.webp',
		title: 'Project management',
		subtext: 'Designed to help business teams manage work with list and calendar views',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	procurement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/5h6go5vn70j05m05824y8l8g0iqp8ul7.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/121o1268bn5tcrinu84vq8e0rk7x011d.webp',
		title: 'Procurement',
		subtext: 'Standardize purchase process on your team from request to receipt',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	softwareDevelopmentKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/f2f8e504020478718ch0h5d335i71na5.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/3p03s867217v4d15ym6v0t71b2m7ea0j.webp',
		title: 'Kanban',
		subtext: 'Visualize work and maximize efficiency with a kanban board',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	softwareDevelopmentScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/84pp4vqo1lh0n35l3b0kwivtc45we78t.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/ofqct7expg2516wj6mu2y460t65p6j5t.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	softwareDevelopmentProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/j4hv2ea8ogdr6gn073ea3q3g1djkcg6a.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/w5uk7mm5bk6uu7s3e4d31i1sg35vok30.webp',
		title: 'Project management',
		subtext: 'Manage and track agile work plus integrate developer tools like GitHub',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	productManagementScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/4534542j3yccx046omhjo83if71k2f7t.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/o01l42c11gbpmkks73ms74es1m2b373p.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	productManagementProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/a612l0d7u28873d6f1ki515ndaa558e4.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/vqnwpmlpj70vmkq32bhqd2nbvd6484g1.webp',
		title: 'Project management',
		subtext: 'Manage and track agile work plus integrate developer tools like GitHub',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	productManagementBusinessCollaboration: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/5st3vp7lc5s7hj3gj7hojuq134d274fa.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/vbhyn84ja67uq4s57ir1123u0303m53c.webp',
		title: 'Business collaboration',
		subtext: 'Designed to help business teams manage work with list and calendar views',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	designKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/t05t6ht0t5vr8c3g1h146pc2uilcdt18.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/r8hgvtjk8b8s710ho47r8l788yi203ah.webp',
		title: 'Kanban',
		subtext: 'Manage design projects efficiently, visualize tasks, and see team workload',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	designWebDesignProcess: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/pvxa3q745o62q3j85hwhqug604hsi8t8.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/oo2102b1cee7uxe6fuk06eam0qrhhf67.webp',
		title: 'Web design process',
		subtext: 'Build web pages plus keep designers and developers on the same page',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	designScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/228vbl2f2vy163kdp43l68356k55bqy1.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/31xe035jg7g1msyvnbqo24crxt8a06kw.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	projectManagementKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/t05t6ht0t5vr8c3g1h146pc2uilcdt18.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/r8hgvtjk8b8s710ho47r8l788yi203ah.webp',
		title: 'Kanban',
		subtext: 'Manage and track agile work plus integrate developer tools like GitHub',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	projectManagementProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/6w2y57obt23x1uppve31yoy7js2kd78k.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/gx3nl2pdo4843583l358b00xr63fsvb3.webp',
		title: 'Project management',
		subtext: 'Designed to help business teams manage work with list and calendar views',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	projectManagementScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/53bk03d638jh563u132bcd110j1m5yf7.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/725277466h2xi7npa3xlhq1716a8tef4.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	itSupportKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1ucnj43iy06p54oty4j7253127ih6g6a.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/m2804ren578556m0h7yaan6a882deye4.webp',
		title: 'Kanban',
		subtext: 'Visualize work and maximize efficiency with a kanban board',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	itSupportScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/rt1rn4by762bwkwf7d1ypscgeyxq65ge.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/wj11o846540c57eo1b7thxc0046cy281.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	itSupportProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/b1c47uky8i2733clcx0x74f73s28mhv4.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/0f85kod814uielg60wx7r7757i036yp7.webp',
		title: 'Project management',
		subtext: 'Designed to help business teams manage work with list and calendar views',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	humanResourcesProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/4534542j3yccx046omhjo83if71k2f7t.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/o01l42c11gbpmkks73ms74es1m2b373p.webp',
		title: 'Project management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	humanResourcesNewEmployeeOnboarding: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/w77sk23hgc4int6ve414dyp3g8kr18dv.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/jrfgj84llgwi47287cq125wj58i803i7.webp',
		title: 'New employee onboarding',
		subtext: 'Manage and standardize the onboarding process for all new hires',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	humanResourcesRecruitment: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/a612l0d7u28873d6f1ki515ndaa558e4.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/vqnwpmlpj70vmkq32bhqd2nbvd6484g1.webp',
		title: 'Recruitment',
		subtext: 'Track potential candidates from application to offer',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	customerServiceKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/0vfjckg342yo7dpig7lya7w257sl0y8j.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/7vm13w8ppnild28m1o7djja6075ixp0i.webp',
		title: 'Kanban',
		subtext: 'Visualize work and maximize efficiency with a kanban board',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	customerServiceScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/7cb404d3xx63w7fyen71f0k0wa5qx872.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/ru71elo3i0u4f28t46gx1tslj7c1aaqu.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	customerServiceProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/v72j33wc0du76m468be7754522a06852.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/3267uqnx00wul5127ga7o5yq748p82yx.webp',
		title: 'Project management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	legalProcessControl: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1r64604wfqibw3yblds6ptf5e4ugd214.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/04a7pb68420724362a8uhtj786i41441.webp',
		title: 'Process control',
		subtext: 'Create, monitor, and iterate on processes in one place',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	legalProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/svgfhl3b5dgx0v375752af1h0cq3guhn.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/0vw4j73qn18wc1j62e2112i084j46evu.webp',
		title: 'Project management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	legalIpInfringement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/42s1w8qhushh0ud8rvkbb60l53oy1y0q.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/kbawcic5d4v78w674008f0200v45vps3.webp',
		title: 'IP Infringement',
		subtext: 'Identify and track IP infringement claims to resolve them.',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	financeProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/f2f8e504020478718ch0h5d335i71na5.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/3p03s867217v4d15ym6v0t71b2m7ea0j.webp',
		title: 'Project Management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	financeProcurement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/xvoae7s27woi8l483jcpuln36yb2up37.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/47v3i1315mrg71nlw8q0giay0x0e46a3.webp',
		title: 'Procurement',
		subtext: 'Standardize purchase processes on your team from request to receipt',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	financeBudgetCreation: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/4es7bdkmylgnm0116x8072ys16p1m306.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/ywgw10hgch0c0m855sgmflpge3mmp7d3.webp',
		title: 'Budget Creation',
		subtext: 'Manage, approve, or reject budget proposals all in one place',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	salesProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/4cu0nh41tq724v7601058vpemb307472.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/71s6di56ekra28pw0riquw7t4a07c575.webp',
		title: 'Project management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	salesSalesPipeline: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/12i72qppy6o120t57c486mifyu1f4m5y.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/8hhhlo7r36kbeo0ysu37ak5546kvxy7h.webp',
		title: 'Sales pipeline',
		subtext: 'Manage and track your customers to help close deals with ease',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	salesLeadTracking: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/m1w76q6ahxw23425oio25br558cdvl53.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/e8nqbp4y2kaf77602168x8gjx1175564.webp',
		title: 'Lead tracking',
		subtext: 'Track sales leads from opportunity through to conversion',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	dataScienceKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1c57w6ld57uw6u6e5444bo3x4dd5h38q.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/hq5t712h2y8d21wtldwdb7w45u4h3g4t.webp',
		title: 'Kanban',
		subtext: 'Manage data science projects efficiently, visualize tasks, and see team workload',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	dataScienceScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/v3heq4ypf18th4023kw4ek2kniv74u54.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/238qe8r16628h6nl7pb54k8fhvf6k8q1.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	dataScienceProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/unkrsmfsv381lyp3riu5046ypr03c2tw.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/7w1u5wl552b5t527b7saxi5lwc766hkx.webp',
		title: 'Project Management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	otherScrum: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/6w2y57obt23x1uppve31yoy7js2kd78k.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/gx3nl2pdo4843583l358b00xr63fsvb3.webp',
		title: 'Scrum',
		subtext: 'Plan, prioritize, and schedule sprints using scrum framework',
		templateType: 'scrum',
		templateId: SCRUM_TEAM_MANAGED_KEY,
	},
	otherKanban: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/f2f8e504020478718ch0h5d335i71na5.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/3p03s867217v4d15ym6v0t71b2m7ea0j.webp',
		title: 'Kanban',
		subtext: 'Visualize work and maximize efficiency with a kanban board',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	otherProjectManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/53bk03d638jh563u132bcd110j1m5yf7.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/725277466h2xi7npa3xlhq1716a8tef4.webp',
		title: 'Project management',
		subtext: 'Manage, track, and assign tasks to streamline project workflows',
		templateType: 'kanban',
		templateId: KANBAN_TEAM_MANAGED_KEY,
	},
	itSupportServiceManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/44bdf0xno116gn46v1hj582x3t3244a3.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/67s8a34cii5ujd0v0m61b157ku6hvr30.webp',
		title: 'IT Help Desk',
		subtext: 'Respond to requests, manage on-call and detect and manage incidents',
		templateType: 'service management',
		templateId: IT_SERVICE_MANAGEMENT_KEY,
		productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
	},
	humanResourcesServiceManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/xmvk304pw25bh5wihcp111fed02v676r.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/pkl88k78m8wiq04pw1olu0pi6k1o2638.webp',
		title: 'HR Help Desk',
		subtext: 'Simplify onboarding, offboarding, and any other HR request',
		templateType: 'service management',
		templateId: HR_SERVICE_MANAGEMENT_KEY,
		productKey: ProductKey.JIRA_SERVICE_MANAGEMENT,
	},
	customerServiceManagement: {
		imageSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb112l7f8707dnq8rsc862u8twt0j305.webp',
		mobileImgSrc:
			'https://dam-cdn.atl.orangelogic.com/AssetLink/52mea2c5jv23r07o6g2mk863sjrirb70.webp',
		title: 'Customer Service Management',
		subtext: 'Help customers get answers, report problems and raise requests',
		templateType: 'service management',
		templateId: CUSTOMER_SERVICE_MANAGEMENT_KEY,
		productKey: ProductKey.JIRA_CUSTOMER_SERVICE,
	},
};
